import { LoaderFunction, redirect, useLoaderData } from "react-router-dom";

import {
  getIncentivesByPlatformLocationIdActiveSignupRewardAnonymous,
  getListingsPublic,
  getPlatformCurrent,
  getReferralCodesAnonymousStatelessShareUrlByPrettyShareToken,
  getUser,
} from "@/api/methods";
import { isAnonymousUser } from "@/modules/anonymousBrowsing/anonymousBrowsingState";
import { ClaimReferralRewardContainer } from "@/modules/referralRewards";
import { getSignupRewardInclusions } from "@/modules/rewards/rewards.utils";
import { waitObj } from "@/toolbox/Promise";

import { queryClient } from "../../Root";

const load = async (request: Request, referralCode: string) => {
  const locationId = new URL(request.url).searchParams.get("locationId");

  const platformLocationId = locationId;

  const platform = await queryClient.fetchQuery({
    ...getPlatformCurrent.keyed(),
    queryFn: () => getPlatformCurrent().catch(() => null),
  });

  const user = await queryClient.fetchQuery({
    ...getUser.keyed(),
    queryFn: () => getUser().catch(() => null),
  });

  const referringPerson = queryClient.fetchQuery({
    ...getReferralCodesAnonymousStatelessShareUrlByPrettyShareToken.keyed({
      prettyShareToken: referralCode,
    }),
  });

  const activeSignupReward = queryClient
    .fetchQuery({
      ...getIncentivesByPlatformLocationIdActiveSignupRewardAnonymous.keyed({
        platformLocationId: platformLocationId ?? "",
      }),
    })
    .catch(() => null);

  const listings = await queryClient.fetchQuery({ ...getListingsPublic.keyed() }).catch(() => []);

  // Get top 4 listings by sell volume
  const topListings = [...(listings || [])]
    .sort((a, b) => (b.sellVolume || 0) - (a.sellVolume || 0))
    .slice(0, 5);

  const activeSignupRewardData = await activeSignupReward;

  const signupRewardInclusions = getSignupRewardInclusions(activeSignupRewardData, listings);

  return waitObj({
    platform,
    referralCode,
    topListings,
    referringPerson,
    activeSignupReward: activeSignupRewardData,
    platformLocationId,
    signupRewardInclusions,
    user,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;

export const claimReferralRewardLoader: LoaderFunction = async ({
  params: { referralCode },
  request,
}) => {
  if (!referralCode) {
    return redirect("/signin");
  }

  const result = await load(request, referralCode);

  if (result.user && !isAnonymousUser(result.user)) {
    return redirect("/");
  }

  return result;
};

export default function ClaimReferralRewardPage() {
  const {
    platform,
    referralCode,
    referringPerson,
    activeSignupReward,
    platformLocationId,
    topListings,
    signupRewardInclusions,
  } = useLoaderData() as LoaderData;

  return (
    <ClaimReferralRewardContainer
      platform={platform!}
      referralCode={referralCode}
      referringPerson={referringPerson}
      activeSignupReward={activeSignupReward!}
      platformLocationId={platformLocationId}
      topListings={topListings}
      signupRewardInclusions={signupRewardInclusions}
    />
  );
}
